import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  Typography,
  Grid,
  Box,
  Container,
  Card,
  CardHeader,
  Divider,
  CardContent,
  Hidden,
  IconButton,
  Toolbar,
  TextField,
  Checkbox,
  ListItemText,
  MenuItem,
  Dialog,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import {
  FileText as FileTextIcon,
  Edit as EditIcon,
  Eye as EyeIcon
} from 'react-feather';
import { connect } from 'react-redux';
import MobileDataList from '../MobileDataList';
import Table from '../Table';
import { CompanyDocumentRes } from '../../proxy/proxy';
import EnumService from '../../services/EnumService';
import { changePage, changeSortModel, refreshData, reset, searchByText, selectStates } from '../../store/actions/companyDocumentActions';
import { RootState } from '../../store/store';
import AppConstants from '../../constants/AppConstants';
import { isDriver, isManager, isOwner } from '../../utils/common';
import LocalStorageService from '../../services/LocalStorageService';

interface Props {
  visibleRows?: Array<CompanyDocumentRes>,
  totalRowCount?: number,
  searchedText?: string,
  selectedWarning?: string,
  currentPage?: number,
  sortModel?: any,
  filterByCol?: any,
  fetchAllRows?: any,
  resetPage?: any,
  filterByText?: any,
  filterByWarning?: any,
  selectedStates: Array<number>,
  setStates: (states: Array<number>) => void,
  onPageChange?: any,
  onSort?: any
}

const CompanyDocuments: React.FC<Props> = ({
  visibleRows = [],
  totalRowCount = 0,
  searchedText,
  currentPage = 0,
  sortModel,
  selectedStates,

  fetchAllRows,
  setStates,
  resetPage,
  filterByText,
  onPageChange,
  onSort
}) => {
  const navigate = useNavigate();
  // const [documents, setDocuments] = useState<CompanyDocumentRes[]>([]);
  // const [totalCount, setTotalCount] = useState<number>(0);
  // const [pageNumber, setPageNumber] = useState<number>(1);
  // const [pageSize, setPageSize] = useState<number>(10);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const localStorageService = LocalStorageService.getService();
  const roles = localStorageService.getUserRoles();
  const showSensitiveInfo = isOwner(roles) || isManager(roles);

  // const fetchDocuments = () => {
  //   try {
  //     CompanyDocumentsService.getAllCompanyDocuments().then((response) => {
  //       setDocuments(response);
  //       setTotalCount(response.length || 0);
  //     });
  //   } catch (error) {
  //     console.log(error);
  //   }
  // };

  useEffect(() => {
    fetchAllRows();
  }, []);

  const onRowClick = (id: number) => {
    if (showSensitiveInfo) {
      navigate(`/app/company/documents/${id}`);
    }
  };

  const onPreviewClick = async (blobSasUrl: string) => {
    // setPreviewUrl(`${blobSasUrl}`);
    window.open(blobSasUrl, '_blank');
  };

  const onClosePreview = () => {
    setPreviewUrl(null);
  };

  const getDocumentType = async (id: number): Promise<string> => {
    const types = await EnumService.getEnums('companyDocumentTypes');
    return types.find((x) => x.id === id)?.value ?? '--';
  };

  const columns = [
    {
      field: 'title',
      headerName: 'Title',
      width: 300
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1
    },
    {
      field: 'companyDocumentType',
      headerName: 'Type',
      width: 200,
      renderCell: (params: any) => {
        const { value } = params;
        const [documentType, setDocumentType] = React.useState<string>('--');

        React.useEffect(() => {
          getDocumentType(value).then(setDocumentType);
        }, [value]);

        return <span>{documentType}</span>;
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      renderCell: (params: any) => {
        const { row } = params;
        return (
          <>
            <IconButton
              size="medium"
              aria-label="preview"
              onClick={() => onPreviewClick(row.blobSasUrl || '')}
            >
              <EyeIcon />
            </IconButton>
            { showSensitiveInfo
              && (
                <IconButton
                  size="medium"
                  aria-label="edit"
                  onClick={() => onRowClick(row.id)}
                >
                  <EditIcon />
                </IconButton>
              )
            }
          </>
        );
      }
    }
  ];

  const refresh = () => {
    fetchAllRows();
  };

  const addDocument = () => {
    navigate('/app/company/documents/add');
  };

  const renderEachMobileCard = (row: CompanyDocumentRes) => (
    <Card key={row.id} sx={{ marginBottom: '10px' }} variant="outlined">
      <CardHeader
        avatar={<FileTextIcon />}
        action={(
          <>
            <IconButton
              aria-label="preview"
              onClick={() => onPreviewClick(row.blobSasUrl || '')}
            >
              <EyeIcon />
            </IconButton>
            { showSensitiveInfo
              && (
                <IconButton
                  aria-label="edit"
                  onClick={() => onRowClick(row.id || 0)}
                >
                  <EditIcon />
                </IconButton>
              )
            }
          </>
        )}
        title={row.title}
        onClick={() => onRowClick(row.id || 0)}
      />
      <Divider />
      <CardContent>
        <Grid container>
          {row.description && (
            <Grid item xs={12}>
              <Typography variant="body2">
                <b>Description:</b> {row.description}
              </Typography>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <>
      <Helmet>
        <title>Company Documents | {AppConstants.APP_NAME}</title>
      </Helmet>
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Card>
          <CardHeader
            action={(
              <>
                <IconButton onClick={refresh}>
                  <RefreshIcon />
                </IconButton>
                { showSensitiveInfo
                  && (
                    <IconButton onClick={addDocument}>
                      <AddIcon />
                    </IconButton>
                  )
                }
              </>
            )}
            title="Company Documents"
          />
          <Divider />
          <CardContent>
            <Hidden mdDown>
              <Table
                rows={visibleRows}
                columns={columns}
                onRowDoubleClick={onRowClick}
                totalRows={totalRowCount}
                page={currentPage}
                onPageChange={onPageChange}
                sortModel={sortModel}
                onSortModelChange={onSort}
              />
            </Hidden>
            <Hidden mdUp>
              <MobileDataList
                rows={visibleRows}
                renderEachRow={renderEachMobileCard}
                totalRows={totalRowCount}
                page={currentPage}
                pageSize={8}
                onPageChange={onPageChange}
              />
            </Hidden>
          </CardContent>
        </Card>
        <Dialog open={!!previewUrl} onClose={onClosePreview} maxWidth="md" fullWidth>
          <DialogTitle>
            PDF Preview
            <IconButton onClick={onClosePreview} sx={{ position: 'absolute', right: 8, top: 8 }}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {/* {previewUrl && <iframe src={previewUrl} width="100%" height="500px" title="PDF Preview" />} */}
            {previewUrl && <iframe src={previewUrl} width="100%" height="500px" title="PDF Preview" />}
          </DialogContent>
        </Dialog>
      </Container>
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  visibleRows: state.companyDocument.filteredRows,
  totalRowCount: state.companyDocument.allRows?.length,
  currentPage: state.companyDocument.currentPage,
  searchedText: state.companyDocument.searchedText,
  sortModel: state.companyDocument.sortModel,
  selectedStates: state.companyDocument.states ?? [],
});

const mapDispatchToProps = (dispatch: any) => ({
  fetchAllRows: () => dispatch(refreshData()),
  resetPage: () => dispatch(reset()),
  filterByText: (text: string) => dispatch(searchByText(text)),
  onPageChange: (page: number) => dispatch(changePage(page)),
  onSort: (sortModel: any) => dispatch(changeSortModel(sortModel)),
  setStates: (states: Array<number>) => dispatch(selectStates(states)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CompanyDocuments);
